/**
 * AOZONE-85: one Slack↔Google identity binding, as returned by the admin-only
 * GET /api/identity-links endpoint. Mirrors the admin-api `IdentityLinkSummary`.
 */
export interface IdentityLink {
  slackAlias: string;
  slackUserId: string;
  googleEmail: string;
  linkedAt: string;
  revoked: boolean;
}

/**
 * AOZONE-85: fetch the current identity links for the admin observability view.
 * The endpoint is session-gated and returns a JSON 401 for non-admins. On a
 * non-ok response the thrown Error carries the HTTP `status` so the caller can
 * redirect a 401 through login (matching the /api/entries flow).
 */
export async function getIdentityLinks(): Promise<IdentityLink[]> {
  const res = await fetch('/api/identity-links');
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  const body = (await res.json()) as { links: IdentityLink[] };
  return body.links;
}

/**
 * AOZONE-93: a freshly minted MCP personal access token, as returned by the
 * POST /api/auth/pat endpoint. The `token` plaintext is shown to the caller
 * exactly once and is never retrievable again.
 */
export interface MintedPatToken {
  token: string;
  googleEmail: string;
  createdAt: string;
  expiresAt?: number;
  /**
   * AOZONE-105 — the new token's list-shaped metadata, so the token page can show
   * the row WITHOUT re-listing. The list endpoint is backed by an
   * eventually-consistent GSI Query (a GSI cannot be read with ConsistentRead), so a
   * refetch immediately after minting can legitimately miss the token just created.
   *
   * OPTIONAL on purpose, and the optionality is LOAD-BEARING — do not "simplify" it
   * to required. Within a single deploy the SPA asset upload and the Lambda update
   * are not ordered, and old bundles persist in browser and CDN caches regardless of
   * deploy order, so this bundle can run against a Lambda that predates the field.
   * Absent → the caller skips the prepend and the row shows up on the next load,
   * which is a cosmetic degradation rather than a broken page.
   */
  summary?: PatSummary;
}

/**
 * AOZONE-96: metadata for one of the caller's active MCP tokens, as returned by
 * GET /api/auth/pats. `id` is the token's opaque hash (safe to expose — it is
 * NOT the secret) and is what `revokePatToken` takes back. The secret is never
 * listed (only its hash is stored server-side).
 */
export interface PatSummary {
  id: string;
  createdAt: string;
  expiresAt?: number;
}

/**
 * AOZONE-96: optional mint parameters. `expiresInDays` requests a SHORTER
 * lifetime than the 90-day default (server rejects anything outside 1..90).
 */
export interface MintPatOptions {
  expiresInDays?: number;
}

/**
 * AOZONE-93/96: mint a new MCP personal access token. POSTs /api/auth/pat
 * (session-gated server-side, minting for the caller's own identity); resolves
 * with the one-time plaintext
 * token (plus its `expiresAt`) on ok, throws an Error carrying the HTTP `status`
 * on a non-ok response. An optional `expiresInDays` requests a shorter lifetime.
 */
export async function postMintPatToken(opts?: MintPatOptions): Promise<MintedPatToken> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  };
  if (opts?.expiresInDays !== undefined) {
    init.body = JSON.stringify({ expiresInDays: opts.expiresInDays });
  }
  const res = await fetch('/api/auth/pat', init);
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return (await res.json()) as MintedPatToken;
}

/**
 * AOZONE-96: list the caller's active MCP tokens (metadata only). GETs
 * /api/auth/pats (session-gated, scoped to the caller's own tokens); throws an
 * Error carrying the HTTP `status` on a non-ok response.
 */
export async function listPatTokens(): Promise<PatSummary[]> {
  const res = await fetch('/api/auth/pats');
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  const body = (await res.json()) as { tokens: PatSummary[] };
  return body.tokens;
}

/**
 * AOZONE-96: revoke one of the caller's MCP tokens by its opaque id. DELETEs
 * /api/auth/pats/{id}; resolves on ok, throws an Error carrying the HTTP
 * `status` on a non-ok response (401 for not-owner/not-found — never HTML).
 */
export async function revokePatToken(id: string): Promise<void> {
  const res = await fetch(`/api/auth/pats/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
}

/**
 * AOZONE-34: thin client for the curated MBR-inclusion toggle. The SPA applies
 * the change optimistically and only calls this to persist; a rejected promise
 * tells the caller to revert.
 */
export async function patchIncludeInMbr(entryId: string, includeInMbr: boolean): Promise<void> {
  const res = await fetch(`/api/entries/${encodeURIComponent(entryId)}/include`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ includeInMbr }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/**
 * AOZONE-32: persist an edited MBR draft for a single entry. An empty string is
 * a valid body (manual authoring / clearing). The SPA applies the change
 * optimistically; a rejected promise tells the caller to revert.
 */
export async function patchMbrDraft(entryId: string, mbrDraftMd: string): Promise<void> {
  const res = await fetch(`/api/entries/${encodeURIComponent(entryId)}/draft`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ mbrDraftMd }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/**
 * AOZONE-59: persist an admin-edited mbrPeriod (the YYYY-MM bucket the entry
 * rolls up to in the MBR report). The server re-validates the format and
 * returns 400 on malformed input — callers should validate client-side first
 * so this only sees well-formed values, but a rejected promise still tells
 * the caller to revert any optimistic update.
 */
export async function patchMbrPeriod(entryId: string, mbrPeriod: string): Promise<void> {
  const res = await fetch(`/api/entries/${encodeURIComponent(entryId)}/period`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ mbrPeriod }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/**
 * AOZONE-35: request an AI regeneration of an entry's MBR draft. The server
 * stamps the draft 'pending' and async-invokes the Rewriter Lambda. The SPA
 * flips the entry to 'pending' optimistically; a rejected promise tells the
 * caller to revert.
 */
export async function postRegenerate(entryId: string): Promise<void> {
  const res = await fetch(`/api/entries/${encodeURIComponent(entryId)}/regenerate`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

/**
 * AOZONE-48 (WS4-4): thin client for POST /api/mbr/generate. Returns a
 * discriminated union; never throws. Status mapping:
 *   200 → ok        (server returned the doc URL + counts)
 *   412 → consent_required (caller redirects the admin through OAuth)
 *   400 → nothing_to_generate (no includeInMbr entries this month)
 *   *   → error     (parse JSON for {error, orphanCopyId?}; fall back to HTTP N)
 */
export type GenerateMbrResult =
  | { kind: 'ok'; docUrl: string; includedCount: number; skippedCount: number }
  | { kind: 'consent_required'; consentUrl: string }
  | { kind: 'nothing_to_generate' }
  | { kind: 'error'; message: string; orphanCopyId?: string };

export interface GenerateMbrOptions {
  period?: string;
  combineWithPrevious?: boolean;
}

export async function postGenerateMbr(
  opts?: GenerateMbrOptions,
): Promise<GenerateMbrResult> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  };
  if (opts && (opts.period || opts.combineWithPrevious)) {
    init.body = JSON.stringify({
      ...(opts.period ? { period: opts.period } : {}),
      ...(opts.combineWithPrevious ? { combineWithPrevious: true } : {}),
    });
  }
  const res = await fetch('/api/mbr/generate', init);
  try {
    if (res.status === 200) {
      const body = (await res.json()) as {
        docUrl: string;
        includedCount: number;
        skippedCount: number;
      };
      return {
        kind: 'ok',
        docUrl: body.docUrl,
        includedCount: body.includedCount,
        skippedCount: body.skippedCount,
      };
    }
    if (res.status === 412) {
      const body = (await res.json()) as { consentUrl: string };
      return { kind: 'consent_required', consentUrl: body.consentUrl };
    }
    if (res.status === 400) {
      // Drain the body to avoid leaks; we don't surface the server message.
      await res.json().catch(() => undefined);
      return { kind: 'nothing_to_generate' };
    }
    const body = (await res.json()) as { error?: string; orphanCopyId?: string };
    const result: GenerateMbrResult = {
      kind: 'error',
      message: body.error ?? `HTTP ${res.status}`,
    };
    if (body.orphanCopyId) result.orphanCopyId = body.orphanCopyId;
    return result;
  } catch {
    return { kind: 'error', message: `HTTP ${res.status}` };
  }
}
